import React from "react";
import { User, Hash, Phone, Mail, MapPin, Calendar, Building, CreditCard, Package } from "lucide-react";
import { FormInput, FormSelect, FormTextarea } from "@/components/ui";

export const VendorForm = ({ form, setForm, errors, handleInputChange, gstRegisteredTypes }) => (
  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300 pb-10 sm:pb-0">
    {/* Section: Core Information */}
    <div className="space-y-4">
      <h3 className="text-[11px] font-black text-brand-blue uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-2">
        <User size={14} /> Core Information
      </h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
        <FormInput
          icon={<User size={14} />}
          label="Company Name"
          required
          placeholder="Vendor name"
          field="name"
          value={form.name}
          onChange={handleInputChange}
          error={errors.name}
        />
        <FormInput
          icon={<Hash size={14} />}
          label="Vendor Code"
          placeholder="e.g. SUP-001 "
          field="code"
          value={form.code}
          onChange={handleInputChange}
          error={errors.code}
        />
        <FormInput
          icon={<Phone size={14} />}
          label="Mobile"
          required
          placeholder="10-digit number"
          field="mobile"
          value={form.mobile}
          onChange={handleInputChange}
          error={errors.mobile}
        />
        <FormInput
          icon={<Mail size={14} />}
          label="Email"
          placeholder="Email address"
          field="email"
          type="email"
          value={form.email}
          onChange={handleInputChange}
          error={errors.email}
        />
        <div className="lg:col-span-2">
          <FormTextarea
            icon={<MapPin size={14} />}
            label="Address"
            required
            placeholder="Full address details..."
            field="address"
            value={form.address}
            onChange={handleInputChange}
            error={errors.address}
          />
        </div>
        <FormInput
          icon={<Calendar size={14} />}
          label="Opening Date"
          required
          field="openingDate"
          type="date"
          value={form.openingDate}
          onChange={handleInputChange}
          error={errors.openingDate}
        />
        <div className="grid grid-cols-2 gap-4">
          <FormInput
            icon={<MapPin size={14} />}
            label="State"
            required
            placeholder="State"
            field="state"
            value={form.state}
            onChange={handleInputChange}
            error={errors.state}
          />
          <FormInput
            icon={<MapPin size={14} />}
            label="District"
            required
            placeholder="District"
            field="district"
            value={form.district}
            onChange={handleInputChange}
            error={errors.district}
          />
        </div>
      </div>
    </div>

    {/* Section: Business & GST Information */}
    <div className="space-y-4 pt-4">
      <h3 className="text-[11px] font-black text-brand-blue uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-2">
        <Building size={14} /> Business & GST Information
      </h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
        <FormSelect
          icon={<Building size={14} />}
          label="GST Registered Type"
          required
          placeholder="Select Registered Type"
          field="gstRegisteredType"
          options={gstRegisteredTypes}
          value={form.gstRegisteredType}
          onChange={handleInputChange}
          error={errors.gstRegisteredType}
        />
        <FormInput
          icon={<Building size={14} />}
          label="GST Number"
          placeholder="e.g. 29AAAAA0000A1Z5"
          field="gstNo"
          value={form.gstNo}
          onChange={handleInputChange}
          error={errors.gstNo}
        />
      </div>
    </div>

    {/* Section: Bank Information */}
    <div className="space-y-4 pt-4">
      <h3 className="text-[11px] font-black text-brand-blue uppercase tracking-widest border-b border-slate-100 pb-2 flex items-center gap-2">
        <CreditCard size={14} /> Bank Account Information
      </h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
        <FormInput
          icon={<CreditCard size={14} />}
          label="Bank Name"
          placeholder="e.g. State Bank of India"
          field="bankName"
          value={form.bankName}
          onChange={handleInputChange}
          error={errors.bankName}
        />
        <FormInput
          icon={<CreditCard size={14} />}
          label="IFSC Code"
          placeholder="e.g. SBIN0001234"
          field="bankIfsc"
          value={form.bankIfsc}
          onChange={handleInputChange}
          error={errors.bankIfsc}
        />
        <FormInput
          icon={<Building size={14} />}
          label="Branch"
          placeholder="e.g. Main Branch"
          field="bankBranch"
          value={form.bankBranch}
          onChange={handleInputChange}
          error={errors.bankBranch}
        />
        <FormInput
          icon={<User size={14} />}
          label="Account Holder"
          placeholder="e.g. John Doe"
          field="bankAccountHolder"
          value={form.bankAccountHolder}
          onChange={handleInputChange}
          error={errors.bankAccountHolder}
        />
        <FormInput
          icon={<CreditCard size={14} />}
          label="Account Number"
          placeholder="e.g. 100021345678"
          field="bankAccountNo"
          value={form.bankAccountNo}
          onChange={handleInputChange}
          error={errors.bankAccountNo}
        />
        <div className="flex flex-col justify-end">
          <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 opacity-80">
            Status
          </label>
          <select
            className={`input h-[44px] font-bold text-xs uppercase tracking-wider bg-slate-50/50 border-slate-200 focus:bg-white transition-all cursor-pointer ${form.active ? "text-emerald-600" : "text-slate-400"}`}
            value={form.active ? "active" : "inactive"}
            onChange={(e) =>
              setForm({
                ...form,
                active: e.target.value === "active",
              })
            }
          >
            <option
              value="active"
              className="text-emerald-600 font-bold"
            >
              Active
            </option>
            <option
              value="inactive"
              className="text-slate-400 font-bold"
            >
              Inactive
            </option>
          </select>
        </div>
      </div>
      <div className="lg:col-span-2">
        <FormTextarea
          icon={<Package size={14} />}
          label="Description"
          placeholder="Type description..."
          field="description"
          value={form.description}
          onChange={handleInputChange}
          error={errors.description}
        />
      </div>
    </div>
  </div>
);
